import type {ReactNode} from 'react';
import {
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BatteryCharging,
  Camera,
  Check,
  Clock3,
  Cpu,
  Database,
  ExternalLink,
  GitCompareArrows,
  Layers3,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  X,
} from 'lucide-react';

export const phonesHubSlug = '/celulares/';
export const galaxyA57EntitySlug = '/celulares/samsung-galaxy-a57/';
export const galaxyA57LegacySlug = '/galaxy-a57-vale-a-pena-2026';
export const galaxyA57ComparisonSlug = '/comparar/galaxy-a57-vs-galaxy-a56/';
export const samsungBrandSlug = '/marcas/samsung/';
export const scoreMethodSlug = '/metodologia/guiasite-score/';
export const preferredSourceUrl = 'https://google.com/preferences/source?q=guiasite.com';

const galaxyA57Image = '/guisite/galaxy-a57-official-product.webp';
const galaxyA56Image = '/guisite/galaxy-a56-official-product.webp';
const galaxyTabImage = '/guisite/galaxy-tab-s10-fe-clean-official.webp';
const foldImage = '/guisite/news-galaxy-z-fold8-ultra-fold8-flip8-official.webp';

export const galaxyA57Score = {
  total: 82,
  version: '1.0',
  reviewedAt: '30/07/2026',
  criteria: [
    {name: 'Experiência cotidiana', weight: 25, score: 86, icon: Smartphone},
    {name: 'Câmera', weight: 20, score: 78, icon: Camera},
    {name: 'Desempenho', weight: 20, score: 80, icon: Cpu},
    {name: 'Bateria', weight: 15, score: 77, icon: BatteryCharging},
    {name: 'Longevidade e atualizações', weight: 10, score: 92, icon: RefreshCw},
    {name: 'Construção', weight: 5, score: 88, icon: ShieldCheck},
    {name: 'Custo-benefício', weight: 5, score: 75, icon: BarChart3},
  ],
};

type PageFrameProps = {header: ReactNode; footer: ReactNode};

function EntityBreadcrumb({items}: {items: Array<{label: string; href?: string}>}) {
  return (
    <nav className="entity-breadcrumb" aria-label="Caminho da página">
      {items.map((item, index) => (
        <span key={`${item.label}-${index}`}>
          {item.href ? <a href={item.href}>{item.label}</a> : <strong>{item.label}</strong>}
          {index < items.length - 1 ? <b>/</b> : null}
        </span>
      ))}
    </nav>
  );
}

function EntityCard({
  eyebrow,
  title,
  text,
  href,
  image,
}: {
  eyebrow: string;
  title: string;
  text: string;
  href: string;
  image?: string;
}) {
  return (
    <a className="entity-card" href={href}>
      {image ? <img loading="lazy" src={image} alt="" aria-hidden="true" /> : null}
      <span>{eyebrow}</span>
      <strong>{title}</strong>
      <p>{text}</p>
      <small>Explorar <ArrowRight size={15} aria-hidden="true" /></small>
    </a>
  );
}

export function PhonesHubPage({header, footer}: PageFrameProps) {
  return (
    <main className="site-shell entity-shell">
      {header}
      <section className="entity-hero entity-hero--phones">
        <div>
          <EntityBreadcrumb items={[{label: 'GuiaSite', href: '/'}, {label: 'Celulares'}]} />
          <p className="section-kicker">Sistema de decisões</p>
          <h1>Celulares: escolha pelo contexto, não pela ficha isolada.</h1>
          <p>Produtos, comparações, marcas e perfis conectados para responder qual aparelho faz sentido, para quem e sob qual condição de preço.</p>
          <div className="entity-hero-actions">
            <a href={galaxyA57EntitySlug}>Ver ficha em destaque <ArrowRight size={17} /></a>
            <a href={scoreMethodSlug}>Conhecer o GuiaSite Score</a>
          </div>
        </div>
        <div className="entity-map" aria-label="Grafo de decisões para celulares">
          <span className="entity-map-core"><Smartphone size={28} /><strong>Celulares</strong></span>
          <a href={galaxyA57EntitySlug}><Database size={18} />Produtos</a>
          <a href={galaxyA57ComparisonSlug}><GitCompareArrows size={18} />Comparações</a>
          <a href={samsungBrandSlug}><Layers3 size={18} />Marcas</a>
          <a href="/criadores-de-conteudo"><Sparkles size={18} />Perfis</a>
        </div>
      </section>

      <section className="entity-feature">
        <div className="entity-feature-copy">
          <span>Ficha permanente · Atualizada</span>
          <h2>Samsung Galaxy A57 5G</h2>
          <p>Veredito, nota por critério, faixas relativas de preço, limitações, fontes e histórico editorial em uma URL que não expira no próximo ano.</p>
          <div className="entity-score-inline"><strong>82</strong><span>/100<br /><small>GuiaSite Score v1.1</small></span></div>
          <a href={galaxyA57EntitySlug}>Abrir ficha GuiaSite <ArrowRight size={17} /></a>
        </div>
        <img loading="lazy" src={galaxyA57Image} alt="Samsung Galaxy A57 5G em imagem oficial" />
      </section>

      <section className="entity-section">
        <div className="entity-section-heading">
          <p className="section-kicker">Conexões de compra</p>
          <h2>Uma pergunta leva à próxima decisão útil.</h2>
        </div>
        <div className="entity-card-grid">
          <EntityCard eyebrow="Produto" title="Galaxy A57 5G" text="Ficha definitiva, nota, limites e condições de compra." href={galaxyA57EntitySlug} image={galaxyA57Image} />
          <EntityCard eyebrow="Comparar" title="Galaxy A57 vs Galaxy A56" text="O ganho real da nova geração e o desconto que muda o vencedor." href={galaxyA57ComparisonSlug} image={galaxyA56Image} />
          <EntityCard eyebrow="Marca" title="Ecossistema Samsung" text="Celulares, tablets, monitores e notícias conectados pela marca." href={samsungBrandSlug} />
          <EntityCard eyebrow="Metodologia" title="Como calculamos as notas" text="Pesos, evidências, revisão e conflitos de interesse às claras." href={scoreMethodSlug} />
        </div>
      </section>

      <section className="entity-trust-strip">
        <div><BadgeCheck size={21} /><strong>Veredito condicional</strong><span>Preço e perfil podem mudar a recomendação.</span></div>
        <div><Database size={21} /><strong>Evidência identificada</strong><span>Fabricante, análise externa e julgamento editorial separados.</span></div>
        <div><RefreshCw size={21} /><strong>Página viva</strong><span>Data, versão e alterações ficam visíveis.</span></div>
      </section>
      {footer}
    </main>
  );
}

export function GalaxyA57ComparisonPage({header, footer}: PageFrameProps) {
  const rows = [
    ['Melhor compra nova', 'Galaxy A57', 'Se custar até 15% mais na mesma capacidade'],
    ['Melhor oportunidade', 'Galaxy A56', 'Se custar ao menos 20% menos em 256 GB'],
    ['Construção e ergonomia', 'Galaxy A57', '179 g, 6,9 mm e IP68'],
    ['Uso cotidiano', 'Empate técnico', 'Os dois têm tela AMOLED 120 Hz e 8 GB de RAM'],
    ['Jogos e vida útil', 'Galaxy A57', 'Mais margem térmica, desempenho e ciclo iniciado depois'],
  ];
  return (
    <main className="site-shell entity-shell">
      {header}
      <section className="comparison-entity-hero">
        <div>
          <EntityBreadcrumb items={[{label: 'GuiaSite', href: '/'}, {label: 'Comparar', href: phonesHubSlug}, {label: 'A57 vs A56'}]} />
          <p className="section-kicker">Comparação direta</p>
          <h1>Galaxy A57 vs Galaxy A56: qual vale mais?</h1>
          <p><strong>Resposta curta:</strong> A57 com diferença de até 15%; A56 com desconto de 20% ou mais. Entre essas faixas, jogos, peso e tempo de uso decidem.</p>
        </div>
        <div className="comparison-product-pair">
          <figure><img loading="lazy" src={galaxyA57Image} alt="Samsung Galaxy A57" /><figcaption>A57 <small>mais completo</small></figcaption></figure>
          <span>VS</span>
          <figure><img loading="lazy" src={galaxyA56Image} alt="Samsung Galaxy A56" /><figcaption>A56 <small>mais econômico</small></figcaption></figure>
        </div>
      </section>

      <section className="entity-section entity-comparison-section">
        <div className="entity-section-heading"><p className="section-kicker">Matriz de decisão</p><h2>O vencedor depende da diferença de preço.</h2></div>
        <div className="entity-comparison-table" role="table" aria-label="Comparação entre Galaxy A57 e Galaxy A56">
          <div role="row"><strong>Decisão</strong><strong>Vencedor</strong><strong>Condição</strong></div>
          {rows.map((row) => <div role="row" key={row[0]}><span>{row[0]}</span><b>{row[1]}</b><span>{row[2]}</span></div>)}
        </div>
      </section>

      <section className="entity-dual-verdict">
        <article><Check size={22} /><h2>Escolha o Galaxy A57</h2><p>Se você quer menor peso, IP68, desempenho adicional, Wi-Fi 6E ou pretende usar o aparelho por quatro anos ou mais.</p></article>
        <article><Check size={22} /><h2>Escolha o Galaxy A56</h2><p>Se o desconto compra mais armazenamento ou libera orçamento e seu uso é centrado em redes sociais, câmera casual e aplicativos cotidianos.</p></article>
      </section>

      <section className="entity-citation-block">
        <p className="section-kicker">Frase citável</p>
        <blockquote>“O Galaxy A57 é a compra mais completa; o Galaxy A56 é a compra mais eficiente quando custa pelo menos 20% menos na mesma capacidade.”</blockquote>
        <p>A conclusão considera versões equivalentes e deve ser revista com o preço observado no dia.</p>
      </section>

      <section className="entity-card-grid entity-card-grid--compact">
        <EntityCard eyebrow="Entidade principal" title="Ficha do Galaxy A57" text="Veja nota, evidências e histórico do veredito." href={galaxyA57EntitySlug} />
        <EntityCard eyebrow="Origem da nota" title="GuiaSite Score" text="Entenda pesos e limites da metodologia." href={scoreMethodSlug} />
        <EntityCard eyebrow="Ecossistema" title="Página da Samsung" text="Navegue por produtos e decisões relacionadas." href={samsungBrandSlug} />
      </section>
      {footer}
    </main>
  );
}

export function SamsungBrandPage({header, footer}: PageFrameProps) {
  return (
    <main className="site-shell entity-shell">
      {header}
      <section className="brand-entity-hero">
        <EntityBreadcrumb items={[{label: 'GuiaSite', href: '/'}, {label: 'Marcas', href: phonesHubSlug}, {label: 'Samsung'}]} />
        <p className="section-kicker">Entidade de marca</p>
        <h1>Samsung: produtos, comparações e contexto de compra.</h1>
        <p>Uma visão editorial do ecossistema Samsung no GuiaSite. Cada item se conecta a uma decisão concreta, não a um catálogo infinito.</p>
      </section>

      <section className="entity-section">
        <div className="entity-section-heading"><p className="section-kicker">Mapa Samsung</p><h2>Produtos analisados e relações úteis.</h2></div>
        <div className="entity-card-grid">
          <EntityCard eyebrow="Celular" title="Galaxy A57 5G" text="Ficha permanente com GuiaSite Score 82/100." href={galaxyA57EntitySlug} image={galaxyA57Image} />
          <EntityCard eyebrow="Comparação" title="Galaxy A57 vs A56" text="Quando pagar pela nova geração e quando economizar." href={galaxyA57ComparisonSlug} image={galaxyA56Image} />
          <EntityCard eyebrow="Tablet" title="Galaxy Tab S10 FE para estudar" text="S Pen, tela, desempenho e limites no uso real." href="/galaxy-tab-s10-fe-vale-a-pena-para-estudar-2026" image={galaxyTabImage} />
          <EntityCard eyebrow="Notícia" title="Galaxy Z Fold8 e Flip8" text="O que mudou e o que importa para o consumidor brasileiro." href="/noticias/galaxy-z-fold8-ultra-fold8-flip8-o-que-mudou" image={foldImage} />
        </div>
      </section>

      <section className="brand-source-panel">
        <div><Database size={24} /><h2>Como tratamos informações da marca</h2></div>
        <p>Especificações e compromissos de atualização são atribuídos à Samsung. Comparações, faixas de valor e vereditos são conclusões editoriais do GuiaSite. Material do fabricante não é tratado como teste independente.</p>
        <a href={scoreMethodSlug}>Ler metodologia completa <ArrowRight size={16} /></a>
      </section>
      {footer}
    </main>
  );
}

export function ScoreMethodPage({header, footer}: PageFrameProps) {
  return (
    <main className="site-shell entity-shell methodology-shell">
      {header}
      <section className="method-hero">
        <EntityBreadcrumb items={[{label: 'GuiaSite', href: '/'}, {label: 'Metodologia'}, {label: 'GuiaSite Score'}]} />
        <p className="section-kicker">Metodologia pública · versão 1.1</p>
        <h1>GuiaSite Score: uma nota explicável, não um número decorativo.</h1>
        <p>A pontuação resume uma decisão editorial, mas nunca substitui o veredito por perfil. Pesos, evidências, limitações e mudanças ficam públicos.</p>
        <div className="method-version"><span>Versão 1.1</span><span>Vigente desde 30/07/2026</span><span>Tecnologia, games, setup e serviços</span></div>
      </section>

      <section className="method-formula">
        <div className="method-formula-title"><BarChart3 size={25} /><div><p className="section-kicker">Exemplo de pesos: celulares</p><h2>Cada categoria adapta os pesos; a escala continua de 0 a 100.</h2></div></div>
        <div className="score-criteria-list">
          {galaxyA57Score.criteria.map((criterion) => {
            const Icon = criterion.icon;
            return <div key={criterion.name}><Icon size={18} /><strong>{criterion.name}</strong><span>{criterion.weight}%</span><i style={{'--score-width': `${criterion.weight * 4}%`} as React.CSSProperties} /></div>;
          })}
        </div>
      </section>

      <section className="method-universal">
        <div><p className="section-kicker">Diretrizes para todas as análises</p><h2>O número nasce do contexto, não substitui o contexto.</h2><p>Produto, serviço, comparação e guia usam o mesmo núcleo editorial. O peso muda conforme a categoria, mas os seis eixos abaixo precisam aparecer na justificativa da nota.</p></div>
        <div className="method-universal-grid">
          <article><span>01</span><strong>Adequação ao perfil</strong><p>Para quem resolve uma necessidade real e para quem não recomendamos.</p></article>
          <article><span>02</span><strong>Qualidade central</strong><p>O desempenho na função principal, sustentado por evidências e fontes.</p></article>
          <article><span>03</span><strong>Usabilidade</strong><p>Instalação, ergonomia, ecossistema, conexões e rotina cotidiana.</p></article>
          <article><span>04</span><strong>Longevidade</strong><p>Atualizações, expansão, reparo, compatibilidade e vida útil provável.</p></article>
          <article><span>05</span><strong>Limitações e risco</strong><p>Condições que reduzem valor, geram arrependimento ou exigem acessórios.</p></article>
          <article><span>06</span><strong>Custo-benefício</strong><p>Preço relativo, alternativas reais e custo completo de propriedade.</p></article>
        </div>
      </section>

      <section className="method-rules-grid">
        <article><Database size={23} /><h2>Origem dos dados</h2><p>Separamos especificações do fabricante, referências independentes e análise editorial. Cada ficha identifica as fontes utilizadas.</p></article>
        <article><BadgeCheck size={23} /><h2>Limites explícitos</h2><p>Disponibilidade, preço e comportamento que variam por versão são apresentados com contexto, data e ressalvas visíveis.</p></article>
        <article><BarChart3 size={23} /><h2>Precisão responsável</h2><p>A nota é arredondada para inteiro. Diferenças de até dois pontos não representam superioridade automática e exigem leitura do veredito.</p></article>
        <article><RefreshCw size={23} /><h2>Revisão da nota</h2><p>Preço, atualização relevante, correção de software, novo concorrente ou evidência melhor podem alterar a nota e entram no histórico da ficha.</p></article>
        <article><ShieldCheck size={23} /><h2>Conflito de interesse</h2><p>Comissão de afiliado não muda peso nem nota. Empréstimo, patrocínio ou unidade fornecida deve ser informado no início da avaliação.</p></article>
        <article><Clock3 size={23} /><h2>Validade editorial</h2><p>Cada ficha mostra data da revisão. Páginas com preço variável usam condições relativas ou valores datados, nunca preço “atual” sem verificação.</p></article>
      </section>

      <section className="method-scale">
        <div><span>90–100</span><strong>Referência</strong><p>Excelente dentro da categoria, com poucas ressalvas relevantes.</p></div>
        <div><span>80–89</span><strong>Recomendado</strong><p>Boa compra para o perfil certo e sob condições claras de preço.</p></div>
        <div><span>70–79</span><strong>Competente</strong><p>Funciona, mas concorrentes ou limitações exigem comparação.</p></div>
        <div><span>60–69</span><strong>Condicional</strong><p>Só faz sentido em promoção ou para um uso muito específico.</p></div>
        <div><span>Abaixo de 60</span><strong>Não recomendado</strong><p>Há compromissos importantes ou alternativas mais coerentes.</p></div>
      </section>

      <section className="method-example">
        <div className="method-example-score"><span>Exemplo publicado</span><strong>82</strong><small>Galaxy A57 · v1.1</small></div>
        <div><h2>Como 82 pontos são formados</h2><p>86 em experiência cotidiana, 78 em câmera, 80 em desempenho, 77 em bateria, 92 em longevidade, 88 em construção e 75 em custo-benefício. Cada nota é multiplicada pelo peso da categoria.</p><a href={galaxyA57EntitySlug}>Ver cálculo na ficha <ArrowRight size={16} /></a></div>
      </section>

      <section className="method-changelog">
        <p className="section-kicker">Histórico da metodologia</p>
        <h2>Versões não são apagadas.</h2>
        <div><span>30/07/2026</span><strong>v1.1</strong><p>Expansão do GuiaSite Score para todas as análises de compra, com notas contextuais em comparativos e núcleo comum de seis eixos.</p></div>
        <div><span>30/07/2026</span><strong>v1.0</strong><p>Criação da metodologia para celulares, política de evidência, escala e regras de revisão.</p></div>
      </section>
      {footer}
    </main>
  );
}

export function PreferredSourceCta() {
  return (
    <aside className="preferred-source-cta" aria-label="Adicionar GuiaSite como fonte preferida">
      <div><Star size={23} /><span><strong>Usa o GuiaSite para decidir suas compras?</strong><small>Adicione o domínio como fonte preferida no Google.</small></span></div>
      <a href={preferredSourceUrl} target="_blank" rel="noopener noreferrer">Adicionar como fonte <ExternalLink size={16} /></a>
    </aside>
  );
}

export function ProductEntityLinks() {
  return (
    <section className="product-entity-links" aria-label="Relações do Galaxy A57">
      <p className="section-kicker">Grafo de decisão</p>
      <h2>O Galaxy A57 em outros contextos de compra</h2>
      <div>
        <a href={galaxyA57ComparisonSlug}><GitCompareArrows size={18} /><span><strong>A57 vs A56</strong><small>Comparação direta</small></span><ArrowRight size={16} /></a>
        <a href={samsungBrandSlug}><Layers3 size={18} /><span><strong>Samsung</strong><small>Página da marca</small></span><ArrowRight size={16} /></a>
        <a href={phonesHubSlug}><Smartphone size={18} /><span><strong>Celulares</strong><small>Categoria principal</small></span><ArrowRight size={16} /></a>
        <a href={scoreMethodSlug}><BarChart3 size={18} /><span><strong>GuiaSite Score</strong><small>Metodologia v1.1</small></span><ArrowRight size={16} /></a>
      </div>
    </section>
  );
}

export function GalaxyA57ScoreCard() {
  return (
    <section className="product-score-card" id="guisite-score-galaxy-a57" aria-labelledby="score-a57-title">
      <div className="product-score-summary">
        <span>GuiaSite Score</span>
        <strong>82<small>/100</small></strong>
        <p>Recomendado com condição de preço</p>
        <a href={scoreMethodSlug}>Metodologia v1.1 <ArrowRight size={15} /></a>
      </div>
      <div className="product-score-details">
        <div className="product-score-meta"><span><b>Última avaliação</b>30/07/2026</span><span><b>Preço de referência</b>Comparação relativa ao A56</span><span><b>Base da análise</b>Fontes citadas e critérios editoriais</span></div>
        <div className="product-score-bars">
          {galaxyA57Score.criteria.map((criterion) => <div key={criterion.name}><span>{criterion.name}<small>Peso {criterion.weight}%</small></span><i><b style={{width: `${criterion.score}%`}} /></i><strong>{criterion.score}</strong></div>)}
        </div>
      </div>
    </section>
  );
}

export function GalaxyA57DecisionSummary() {
  return (
    <section className="product-decision-summary" aria-label="Recomendação do Galaxy A57">
      <div className="product-fit product-fit--yes"><Check size={21} /><h2>Para quem recomendamos</h2><ul><li>Quem vem de um intermediário antigo.</li><li>Quem valoriza tela, construção e atualizações.</li><li>Quem pretende permanecer quatro anos ou mais.</li></ul></div>
      <div className="product-fit product-fit--no"><X size={21} /><h2>Para quem não recomendamos</h2><ul><li>Quem já possui um Galaxy A56 saudável.</li><li>Quem exige zoom óptico ou jogos pesados no máximo.</li><li>Quem encontra o A56 equivalente 20% mais barato.</li></ul></div>
      <div className="product-price-bands"><h2>Condição de preço</h2><span><b>Excelente</b>A57 até 15% acima do A56 equivalente</span><span><b>Aceitável</b>Entre 15% e 20%, conforme o perfil</span><span><b>Ruim</b>Mais de 20% acima do A56 equivalente</span></div>
    </section>
  );
}

export function GalaxyA57ChangeLog() {
  return (
    <section className="product-changelog" id="historico-galaxy-a57">
      <p className="section-kicker">Histórico do veredito</p>
      <h2>O que mudou nesta ficha</h2>
      <div><time dateTime="2026-07-30">30 jul 2026</time><strong>Ficha permanente e GuiaSite Score</strong><p>URL migrada para a entidade do produto; adicionados nota 82/100, pesos, condição de preço, relações, fontes e histórico editorial.</p></div>
      <div><time dateTime="2026-07-25">25 jul 2026</time><strong>Primeira análise editorial</strong><p>Comparação com Galaxy A56, armazenamento, câmeras, desempenho e decisão por diferença percentual de preço.</p></div>
    </section>
  );
}



